/**
 * Potted — site-wide constants.
 *
 * Everything referenced in more than one place lives here: nav, footer, labels,
 * the launch date, the timezone, and the share format. A component NEVER hardcodes
 * any of it (CLAUDE.md §6).
 *
 * This file holds DATA. Logic that consumes it (day-key arithmetic, share-string
 * composition) belongs to DLY and SHR in Phase 3.
 */
import { RELEASES } from './releases.ts';
import { THEMES } from './themes.ts';

export const SITE = {
  name: 'Potted',
  tagline: 'A daily pool puzzle.',
  description: 'One table a day, the same for everyone. Clear it in as few shots as you can.',
  /** The site's own domain (owner, 2026-09-26, PRD D-041). potted.pages.dev still works. */
  url: 'https://playpotted.com',
  locale: 'en-AU',
} as const;

/**
 * The day rolls over at 5:00 pm Melbourne time (owner, 2026-09-25, PRD D-027). Always the
 * IANA zone via `Intl.DateTimeFormat` — never a hardcoded UTC offset, because AEST/AEDT
 * moves: 5 pm Melbourne is 07:00 UTC in winter and 06:00 UTC in summer.
 *
 * WHICH day it is comes from the server's clock, never the browser's (PRD N7): a player
 * who moves their clock forward still only ever receives today's table.
 */
export const TIMEZONE = 'Australia/Melbourne';

/*
 * The rollover itself (5 pm, and which table is today's) is computed only on the server,
 * in apps/api/src/clock.ts (DLY-001). The browser never works out the day.
 */

/**
 * Puzzle number = days since LAUNCH.date, plus one: that day is #1. Once results have
 * been shared this value can NEVER change (PRD D-003).
 *
 * 🔒 Set by the owner on 2026-09-27 (PRD D-048, replacing D-034's 1 October): the first
 * daily table, 24 September 2026, is puzzle #1. The site opens to the public on
 * 27 September (#4), with the three days before it already in the calendar. `isPlaceholder` stays as a
 * switch: while true, no number is shown anywhere.
 */
export const LAUNCH = {
  date: '2026-09-24',
  isPlaceholder: false,
} as const;

/** Day keys are `YYYY-MM-DD` in TIMEZONE. */
export const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Routes. Keep in step with docs/routes.md, in the same commit (CLAUDE.md §6).
 *
 * Only `today` is a page today. Since DLY-005 the archive and a past day are a calendar
 * popup at the bare `/` (the address bar stays clean, URL-001), so `archive` and `day`
 * are reserved names, not addresses the app serves; nothing renders `NAV` yet.
 */
export const ROUTES = {
  today: '/',
  archive: '/archive',
  about: '/about',
  day: '/day/:dateKey',
  privacy: '/privacy',
  terms: '/terms',
  refunds: '/refunds',
} as const;

/**
 * The document pages (PRV-001, PRV-004), each its own HTML entry so it has an address.
 * Linked from How to play and from the foot of every document page: Paddle's review
 * wants terms, refunds and privacy "clearly accessible via navigation".
 */
export const ALL_DOCUMENT_LINKS = [
  { id: 'about', label: 'About', href: ROUTES.about },
  { id: 'privacy', label: 'Privacy', href: ROUTES.privacy },
  { id: 'terms', label: 'Terms', href: ROUTES.terms },
  { id: 'refunds', label: 'Refunds', href: ROUTES.refunds },
] as const;

export type DocumentId = (typeof ALL_DOCUMENT_LINKS)[number]['id'];

/** The document pages that exist: Refunds only once there is something to refund (D-047). */
export function documentLinks(
  packReleased: boolean,
): readonly (typeof ALL_DOCUMENT_LINKS)[number][] {
  return ALL_DOCUMENT_LINKS.filter((d) => packReleased || d.id !== 'refunds');
}

export const DOCUMENT_LINKS = documentLinks(RELEASES.supporterPack);

export const NAV = [
  { label: 'Today', href: ROUTES.today },
  { label: 'Archive', href: ROUTES.archive },
  { label: 'About', href: ROUTES.about },
] as const;

export const FOOTER = {
  note: 'A daily pool puzzle. New table at 5pm, Melbourne time.',
  links: [{ label: 'About', href: ROUTES.about }],
} as const;

/**
 * Scoring labels, config-driven so they can be renamed freely (PRD §4).
 *
 * `delta` is score − par. Rules are evaluated in order; the first whose `atMost`
 * is ≥ delta wins, so the final entry must be the catch-all.
 */
export const SCORE_LABELS = [
  { atMost: -2, label: 'Shark' },
  { atMost: -1, label: 'Runout' },
  { atMost: 0, label: 'Table Run' },
  { atMost: 2, label: 'Chalk Up' },
  { atMost: Number.POSITIVE_INFINITY, label: 'Railbird' },
] as const;

/**
 * Share format. No spoilers: no ball positions, no layout, nothing about which
 * pockets were used. SHR-001 composes the string from these pieces in Phase 3.
 *
 *   Potted #132 · 9ft · 7 balls
 *   5 shots (Par 6) · Runout
 *   🟢🟢⚪🟢🔴🟢
 */
export const SHARE = {
  separator: ' · ',
  /** In the order the key lists them. */
  shotEmoji: {
    /** One ball potted on this shot. */
    potted: '🟢',
    /** Two or more balls potted on one shot (owner, 2026-09-25): what beats par. */
    multi: '🟡',
    /** No pot. */
    miss: '⚪',
    /** Cue ball potted: the shot plus a penalty, so 2 strokes. Outranks a pot. */
    scratch: '🔴',
  },
} as const;

/** What each shot colour means: the key on the results screen and in How to play. */
export const SHOT_KEY = {
  potted: 'Potted a ball',
  multi: 'Potted 2 or more',
  miss: 'No pot',
  scratch: 'Scratch: +2 strokes',
} as const;

/** The calendar and the archive (DLY-005). */
export const CALENDAR_COPY = {
  open: 'Calendar',
  title: 'Calendar',
  close: 'Close calendar',
  previous: 'Previous month',
  next: 'Next month',
  weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  legend: {
    under: 'Under par',
    par: 'Par',
    over: 'Over par',
    unfinished: 'Not cleared',
  },
  today: 'Today',
  notPlayed: 'not played',
  notOut: 'not out yet',
  playing: 'playing now',
  /** Before launch; \`date\` is the first table's date, e.g. "1 October". */
  notYet: (date: string): string => `The first daily table is on ${date}.`,
  archiveNote: 'Past days are just for fun: they never count towards your streak or statistics.',
  /** The header tag while playing a past day. */
  archiveTag: 'Archive',
  archiveKicker: "Archive: doesn't count",
  backToToday: 'Today',
  backToTodayLabel: "Back to today's table",
} as const;

/** The Statistics popup (STA-002, owner 2026-09-25). */
export const STATS_COPY = {
  open: 'Statistics',
  title: 'Statistics',
  close: 'Close statistics',
  played: 'Played',
  cleared: 'Cleared %',
  currentStreak: 'Current streak',
  bestStreak: 'Best streak',
  average: 'Average vs par',
  level: 'Level',
  noAverage: '—',
  results: 'Results',
  nextTable: 'Next table in',
  firstTable: 'First daily table in',
  ready: 'A new table is ready.',
  playIt: 'Play it',
  /** Stated plainly, as the plan's risk table asks (phase-3-plan §8). */
  deviceNote: 'Statistics are kept on this device only. Clearing your browser data clears them.',
  /** ACC-011: signed in, the numbers are the account's. */
  accountNote: 'Signed in: these are your account’s statistics, the same on every device.',
} as const;

/**
 * The How to play popup (HTP-001, reworked Wordle-style as HTP-002). Plain words; the
 * player may never have played pool. The colours are named because the shot colours
 * are the same in every theme (tokens.css) and match the share emoji.
 */
export const HOW_TO_PLAY = {
  open: 'How to play',
  title: 'How to play',
  close: 'Close how to play',
  lede: 'Clear the table in as few strokes as you can.',
  rules: [
    'Tap the table to aim there, or drag anywhere to turn the aim. Fine-tune with the arrows.',
    'Set the power, and tap or drag the small white ball beside Shoot to add spin. Then press Shoot. You only ever hit the white ball.',
    'Every shot is one stroke.',
  ],
  examplesHeading: 'Examples',
  examples: {
    potted: { colour: 'Green', text: 'a ball went in.' },
    multi: {
      colour: 'Gold',
      text: 'two or more balls went in with one shot. That is how you beat par.',
    },
    miss: { colour: 'Grey', text: 'nothing went in. It still counts as a stroke.' },
    scratch: {
      colour: 'Red',
      text: 'the white went in (a scratch). That shot counts as 2 strokes, and the white goes back where it was.',
    },
  },
  parHeading: 'Par',
  par: 'Par is the strokes a perfect run takes: one ball every shot. How you finish against it gives your result.',
  resultsHeading: 'Your result against par',
  daily: 'A new table every day at 5pm, Melbourne time.',
  documents: 'About this site',
} as const;

/**
 * The privacy page, `/privacy` (PRV-001, owner 2026-09-26). Plain English, and only what
 * is true of the code today: update it in the same commit as anything that changes what
 * is stored or sent (docs/access-patterns.md is the full list).
 *
 * Turnstile's Invisible mode is on the condition that this page refers to Cloudflare's
 * Turnstile Privacy Addendum; a test checks the link is here.
 */
export const TURNSTILE_PRIVACY_URL = 'https://www.cloudflare.com/turnstile-privacy-policy/';

/** PRV-003: the sign-in provider's own policy (D-042). */
export const WORKOS_PRIVACY_URL = 'https://workos.com/legal/privacy';

/** Privacy questions (owner, 2026-09-26): forwarded to the owner by Cloudflare Email Routing. */
export const PRIVACY_EMAIL = 'privacy@playpotted.com';

export const PRIVACY = {
  title: 'Privacy',
  updated: 'Last updated 27 September 2026',
  lede: 'Potted is a daily pool puzzle. It has no analytics and no tracking. You never need an account to play; signing in is optional.',
  back: 'Back to the game',
  sections: [
    {
      heading: 'On your device',
      body: [
        'Your settings, the shots you took on each daily table, your scores and your statistics are saved in your browser, so the game can pick up where you left off. They stay on your device.',
        'Clearing this site’s data in your browser removes them.',
      ],
    },
    {
      heading: 'What reaches our server',
      body: [
        'When you play the daily table, each shot is sent to our server as you take it: the aim, power and spin, and nothing else. The server plays the shot itself and keeps the result, so a result cannot be faked. For each day you play it keeps your shots, the time each one arrived, and the result.',
        'Unless you choose to sign in, it does not know your name or your email address. It never stores your IP address.',
        'Practice games and past days from the calendar are never sent.',
      ],
    },
    {
      heading: 'Cookies',
      body: [
        'The first time you play a daily table, the server gives your browser a cookie, potted_player. It is a long random code that lets the server recognise the same player from one day to the next, so the first attempt at each table is the one that counts. It lasts up to 400 days.',
        'If you sign in, your browser also gets potted_session, which keeps you signed in for 90 days and is renewed while you play, and, for the ten minutes a sign-in takes, potted_signin, which makes sure a sign-in finishes in the browser that started it.',
        'Only our server can read these cookies, and they are only sent to our game server. Our server keeps only a scrambled (hashed) form of each code. None of them is used for advertising or to follow you anywhere else. Clear them and you are simply signed out, or a new player.',
      ],
    },
    {
      heading: 'If you sign in',
      body: [
        'Signing in is optional. It keeps your results, statistics and streak the same on every device you play on. There is no password: you type your email and a sign-in code is emailed to you, or you choose Continue with Google.',
        'If you choose Google, Google tells WorkOS your name, your email address and that Google has checked it, as described in Google’s privacy policy. Our server keeps only the email address, as below, never your Google password or anything else from your Google account.',
        'The sign-in page and the emailed code are provided by WorkOS, which checks that the email is yours and then tells our server. WorkOS keeps your email address and a record of your sign-ins, as described in its privacy policy.',
        'Our server then keeps your email address (to show which account you are signed in as), WorkOS’s id for you, when each of your sign-ins started and was last used, and which days you played. Your email is never shown to anyone else, and never used for marketing.',
      ],
      link: { label: 'WorkOS privacy policy', href: WORKOS_PRIVACY_URL },
    },
    {
      heading: 'Deleting your account',
      body: [
        'In Settings, under Account, choose Delete account. Your email address and every sign-in are deleted from our server straight away, and we ask WorkOS to delete its record of you too. If WorkOS cannot be reached, nothing is deleted and you are asked to try again, so an account is never left half-deleted.',
        'The days you played stay on your device, and on our server only as anonymous totals in the day’s numbers, no longer linked to you.',
      ],
    },
    {
      heading: 'The day’s numbers',
      body: [
        'After you clear a table you may see how many people played that day and what share cleared it. These are totals only. Nobody else can see your result.',
      ],
    },
    {
      heading: 'Cloudflare',
      body: [
        'The game is hosted by Cloudflare, which handles every request to the site, as any web host does, to deliver the pages and protect them from attack.',
        'To keep automated scripts from flooding the game, a new player’s first shot passes Cloudflare Turnstile, an invisible check that the player is a person. You do not see it or have to do anything. Turnstile collects some information from your browser to do this, as described in Cloudflare’s Turnstile Privacy Addendum, and our server passes your IP address to Cloudflare with the check, as Cloudflare recommends.',
        'For the same reason, our server counts how many requests arrive from each IP address over the last minute or hour, in memory only. Those counts are never saved and are thrown away as each minute or hour passes.',
      ],
      link: { label: 'Cloudflare Turnstile Privacy Addendum', href: TURNSTILE_PRIVACY_URL },
    },
    {
      heading: 'Changes',
      body: [
        'If what the game stores or sends ever changes, for example when purchases arrive, this page will say so first, with a new date at the top.',
      ],
    },
    {
      heading: 'Questions',
      body: [
        'For anything about your privacy or this page, email us. If you have an account, write from the email you sign in with and we can find it. Without an account the game never knows who you are, so we cannot look up your records by name or email; clearing this site’s data in your browser removes everything on your device and cuts the link to anything on our server.',
      ],
      link: { label: PRIVACY_EMAIL, href: `mailto:${PRIVACY_EMAIL}` },
    },
  ],
} as const;

/** PRV-004 (owner, 2026-09-27): support and refunds, forwarded by Cloudflare Email Routing. */
export const SUPPORT_EMAIL = 'support@playpotted.com';

/** Paddle sells the unlock as Merchant of Record (D-044); its buyer terms govern the sale. */
export const PADDLE_BUYER_TERMS_URL = 'https://www.paddle.com/legal/buyer-terms';

/**
 * The supporter unlock (D-043). The base price only: Paddle shows each buyer the local
 * price for their country, tax included, at checkout (sandbox catalogue, 2026-09-26).
 */
export const SUPPORTER = {
  basePrice: 'US$3.99',
  refundDays: 14,
} as const;

/** How many themes the pack unlocks. */
const PACK_SIZE = THEMES.filter((theme) => theme.tier === 'premium').length;

/** The terms of the purchase: shown only once the supporter pack is released (D-047). */
const PURCHASE_TERMS: DocumentCopy['sections'] = [
  {
    heading: 'Premium themes',
    body: [
      `Premium themes are an optional one-off purchase of ${SUPPORTER.basePrice}. Checkout shows the price in your local currency, including any tax. It is a single payment, not a subscription.`,
      `It unlocks all ${String(PACK_SIZE)} premium table themes on your account, on every device you sign in on. They change only how the table looks: they give no advantage in the game. You need an account to buy them.`,
    ],
  },
  {
    heading: 'Payment',
    body: [
      'Our order process is conducted by our online reseller Paddle.com. Paddle.com is the Merchant of Record for all our orders. Paddle provides all customer service inquiries and handles returns.',
      'Your payment details go to Paddle, never to us. Paddle’s buyer terms apply to the purchase.',
    ],
    link: { label: 'Paddle buyer terms', href: PADDLE_BUYER_TERMS_URL },
  },
  {
    heading: 'Refunds',
    body: [
      `Changed your mind? Ask within ${SUPPORTER.refundDays} days of buying and you get a full refund, no questions asked. The refund page says how.`,
    ],
    link: { label: 'Refund policy', href: ROUTES.refunds },
  },
];

/**
 * The terms page, `/terms` (PRV-004). The owner chose the brand name alone, with no
 * personal name or ABN (2026-09-27): Paddle's review asks for "the company name or sole
 * proprietor's brand". Not legal advice; plain English, and only what the game does.
 */
export function termsCopy(packReleased: boolean): DocumentCopy {
  return {
    title: 'Terms',
    updated: 'Last updated 27 September 2026',
    lede: packReleased
      ? 'These terms cover playing Potted at playpotted.com and buying its premium themes. They are short on purpose.'
      : 'These terms cover playing Potted at playpotted.com. They are short on purpose.',
    back: 'Back to the game',
    sections: [
      {
        heading: 'Who we are',
        body: [
          'Potted (“we” and “us”) is a daily pool puzzle, run from Melbourne, Australia. By playing, you agree to these terms.',
        ],
      },
      {
        heading: 'Playing',
        body: [
          'Potted is free to play, and you never need an account. A new table arrives every day at 5pm, Melbourne time.',
          'Play fairly. Do not use scripts or bots to play, and do not try to break, overload or get around the game’s servers or checks. Results made that way may be removed.',
        ],
      },
      {
        heading: 'Accounts',
        body: [
          `An account is optional. It keeps your results the same on every device${packReleased ? ', and it is where a purchase is kept' : ''}. You sign in with a code sent to your email, so keep that email account secure. You can delete your account at any time in Settings.`,
        ],
      },
      ...(packReleased ? PURCHASE_TERMS : []),
      {
        heading: 'Your rights',
        body: [
          'Nothing in these terms takes away rights you have under consumer law, including the Australian Consumer Law and the laws of the country you live in.',
          'Otherwise, the game is provided as it is. We work to keep it running and fair, but we cannot promise it will always be available or free of mistakes, and, as far as the law allows, we are not liable for losses from using it.',
        ],
      },
      {
        heading: 'Changes',
        body: [
          'We may change the game, and these terms. If the terms change, this page will show a new date at the top. These terms are governed by the laws of Victoria, Australia.',
        ],
      },
      {
        heading: 'Contact',
        body: [
          packReleased
            ? 'For help with the game or a purchase, email us.'
            : 'For help with the game, email us.',
        ],
        link: { label: SUPPORT_EMAIL, href: `mailto:${SUPPORT_EMAIL}` },
      },
    ],
  };
}

export const TERMS: DocumentCopy = termsCopy(RELEASES.supporterPack);

/** The refund policy, `/refunds` (PRV-004). Owner, 2026-09-27: 14 days, no questions. */
export const REFUNDS = {
  title: 'Refunds',
  updated: 'Last updated 27 September 2026',
  lede: `If you buy the premium themes and change your mind, ask within ${SUPPORTER.refundDays} days and you get your money back in full. No questions asked.`,
  back: 'Back to the game',
  sections: [
    {
      heading: 'How to ask',
      body: [
        'Email us from the address on your receipt, or reply to the receipt Paddle emailed you. Include the order number from the receipt if you have it.',
        'You can also ask Paddle directly at paddle.net, which handles every order for us.',
      ],
      link: { label: SUPPORT_EMAIL, href: `mailto:${SUPPORT_EMAIL}` },
    },
    {
      heading: 'What happens next',
      body: [
        'Paddle refunds the full amount to the card or account you paid with. It usually shows up within 5 to 10 working days, depending on your bank.',
        'When the refund goes through, the premium themes are removed from your account, and the table goes back to a free theme.',
      ],
    },
    {
      heading: `After ${SUPPORTER.refundDays} days`,
      body: [
        'If the themes do not work, or the law where you live gives you a right to a refund, you still get one, whenever you ask. This includes your rights under the Australian Consumer Law.',
      ],
    },
    {
      heading: 'The purchase',
      body: [
        `The premium themes are a one-off purchase of ${SUPPORTER.basePrice}, or the local price shown at checkout. Paddle.com is the Merchant of Record for all our orders, and its buyer terms apply.`,
      ],
      link: { label: 'Paddle buyer terms', href: PADDLE_BUYER_TERMS_URL },
    },
  ],
} as const;

/**
 * The About page, `/about` (PAGE-001; routes.md since Phase 3). What the game is and how
 * a table is made, in plain words, including the honest note on par (K-02): par is the
 * best line the solver found, not a proven minimum.
 */
export const ABOUT = {
  title: 'About Potted',
  updated: 'A daily pool puzzle, made in Melbourne',
  lede: 'One pool table a day, the same for everyone. Clear it in as few strokes as you can.',
  back: 'Back to the game',
  sections: [
    {
      heading: 'The game',
      body: [
        'Every day at 5pm, Melbourne time, a new table appears: a few balls, a white ball, and six pockets. You hit only the white. Pot every ball, and the fewer strokes it takes, the better your result.',
        'There is no timer and no rush. Take as long as you like over each shot, then share your result with friends: just the shots, never the table, so nobody is spoiled.',
        'Missed a day? Past tables are in the calendar to play for fun. Only the day’s own table counts towards your result and your streak.',
      ],
    },
    {
      heading: 'Real physics',
      body: [
        'The balls move as they would on a real table: they slide, then roll, pick up spin, bounce off cushions at the right angles, and drop into pockets that play like real ones. Topspin follows through, backspin draws back, and side spin changes the angle off a cushion.',
        'The physics gives exactly the same result on every phone and computer, so everyone plays the same table in exactly the same way.',
      ],
    },
    {
      heading: 'How a table is made',
      body: [
        'Tables are made ahead of time by a program that deals the balls fairly: never touching, never blocking a pocket, and never crowding the white. There are three sizes of table, 7, 8 and 9 foot, and each comes up about a third of the time.',
        'The week gets harder as it goes: five balls on a Monday, up to eight at the weekend.',
        'A future table cannot be worked out from the game, or peeked at early: each one stays private until its day.',
      ],
    },
    {
      heading: 'Par',
      body: [
        'Par is one stroke per ball: a perfect run, potting a ball with every shot. Beat it by potting two or more balls with one shot.',
        'Before a table goes out, a solver plays it and checks it can be cleared. Where even the solver needed an extra shot, par is its count instead, so every par is one you can reach. The solver is good, not perfect: par is the best line it found, and a cleverer line may beat it.',
      ],
    },
    {
      heading: 'Fair for everyone',
      body: [
        'Your phone never tells the server your score. It sends only the shots you took, and the server plays them itself on the same physics, so a result cannot be faked.',
        'You never need an account to play. Signing in is optional, and only keeps your results the same on every device.',
      ],
    },
  ],
} as const;

/** One document page's copy: the shape ABOUT, PRIVACY, TERMS and REFUNDS share. */
export interface DocumentCopy {
  readonly title: string;
  readonly updated: string;
  readonly lede: string;
  readonly back: string;
  readonly sections: readonly {
    readonly heading: string;
    readonly body: readonly string[];
    readonly link?: { readonly label: string; readonly href: string };
  }[];
}

/** Every document page, by the `data-document` its HTML carries. */
export const DOCUMENTS: Readonly<Record<DocumentId, DocumentCopy>> = {
  about: ABOUT,
  privacy: PRIVACY,
  terms: TERMS,
  refunds: REFUNDS,
};

/**
 * Singular/plural pairs, for copy that counts things in a sentence.
 *
 * Written out rather than derived, because "penalty" does not pluralise by adding an
 * s and a naive helper shipped "2 penaltys" to the win screen. English is data here,
 * not logic.
 */
/** How a score reads against par, on the win screen (Phase 2). */
export const PAR_WORDS = {
  level: 'level par',
  over: 'over',
  under: 'under',
  orMore: 'or more',
} as const;

export const COUNTS = {
  shot: ['shot', 'shots'],
  penalty: ['penalty', 'penalties'],
  stroke: ['stroke', 'strokes'],
  ball: ['ball', 'balls'],
} as const;

/** UI-022: the tip a device sees until its first shot ever (ui/first-visit.ts). */
export const FIRST_VISIT_HINT = {
  par: (par: number): string => `Par is ${String(par)}.`,
  aim: 'Tap or drag on the table to aim, then press Shoot.',
  dismiss: 'Dismiss tip',
} as const;

/** Loading today's table, and what the page says when it cannot (DLY-003). */
export const TODAY_COPY = {
  loading: "Racking up today's table…",
  unavailableTitle: "Couldn't load today's table",
  unavailable: 'Check your connection, then try again.',
  outOfDateTitle: 'Potted has been updated',
  outOfDate: "Reload to get the latest version before today's table.",
  retry: 'Try again',
  reload: 'Reload',
  /** The header tag on a practice table, before the first daily one. */
  practiceTag: 'Practice',
  /** `date` is the first table's date, e.g. "1 October". */
  practiceNote: (date: string): string =>
    `Practice table. The daily table starts on ${date}; this one doesn't count.`,
} as const;

/** The results screen (SHR-001). */
export const RESULT_COPY = {
  cleared: 'Table cleared',
  copy: 'Copy result',
  copied: 'Copied to clipboard',
  /** SHR-003: share straight to X or Facebook. SHR-004: Copy result carries the link. */
  shareX: 'X',
  shareXLabel: 'Share on X',
  shareFacebook: 'Facebook',
  shareFacebookLabel: 'Share on Facebook',
  copyFailed: 'Could not copy. Your browser blocked the clipboard.',
  calendar: 'Calendar',
  comingSoon: 'Coming soon',
  newTable: 'New table',
  /** After the attempt that counts (D-029, DLY-004). */
  practice: 'Practice this table',
  practiceAgain: 'Practice again',
  /** An archive day, again (DLY-005). */
  playAgain: 'Play again',
  practiceKicker: "Practice: doesn't count",
  /** UI-012: leaving the results for free play on the empty table, and coming back. */
  close: 'Close',
  showResult: 'Result',
  statistics: 'Statistics',
  shotsLabel: 'Your shots',
  /** "Shot 3: Potted a ball", read out for each dot. */
  shot: 'Shot',
} as const;

/** UI-010: the small button on the spin ball that puts the tip back in the middle. */
export const SPIN_COPY = {
  reset: 'Centre ball',
} as const;

/** ACC-005: the Account section at the top of Settings. */
export const ACCOUNT_COPY = {
  heading: 'Account',
  /** ACC-008: the header button, and the popup's close button. */
  open: 'Account',
  openSignedIn: 'Account: signed in',
  close: 'Close account',
  pitch: 'Sign in with your email to keep your results and streak on every device. No password.',
  signIn: 'Sign in',
  signedInAs: 'Signed in as',
  signOut: 'Sign out',
  signOutAll: 'Sign out of all devices',
  delete: 'Delete account',
  deleteConfirm:
    'Delete your account? Your email and sign-ins are deleted. Your results stay on this device.',
  deleteYes: 'Delete',
  deleteNo: 'Keep it',
  deleteFailed: 'Could not delete right now, so nothing was deleted. Try again in a minute.',
  signInFailed: "Sign-in didn't finish. Please try again.",
  unavailable: 'Accounts are coming soon.',
  offline: 'Could not reach the server. Try again in a moment.',
} as const;

/** The day's numbers under a counted result (API-005): "1,284 players today · 71% cleared". */
export const DAY_NUMBERS_COPY = {
  players: ['player today', 'players today'],
  cleared: 'cleared',
} as const;

/** Copy that appears in more than one place. */
export const LABELS = {
  par: 'Par',
  shots: 'Shots',
  ballsRemaining: 'Balls left',
  /** UI-012: shots on the cleared table, which count for nothing. */
  freePlay: 'Free play',
  penalty: 'Penalty',
  scratch: 'Scratch',
  puzzleNumber: 'Puzzle',
  nextPuzzle: 'Next puzzle',
  share: 'Share result',
  restart: 'Restart',
} as const;

/** The Settings popup (UI-004). */
export const SETTINGS_COPY = {
  open: 'Settings',
  title: 'Settings',
  close: 'Close settings',
  done: 'Done',
  tableHeading: 'Table',
  /** UI-015. */
  showCue: 'Show the pool cue',
  /** UI-018. */
  cueLength: 'Pool cue length',
  /** UI-019. */
  cueStyle: 'Pool cue',
  orientation: 'Table orientation',
  orientations: {
    landscape: 'Landscape',
    portrait: 'Portrait',
  },
  /** UI-011: on a phone, in place of the choice. */
  orientationFollowsPhone: 'Follows your phone. Turn it sideways for a landscape table.',
  tableScale: 'Table size',
  soundHeading: 'Sound',
  effects: 'Sound effects',
  themeHeading: 'Theme',
  premium: 'Premium',
} as const;
